import { Tween } from "@westpierstudio/luma/createjs";
import { View } from "@westpierstudio/luma/mvc";
import { Flow } from "@westpierstudio/luma/utils/flow";
import { GameServices } from "game/GameServices";
import { Spine } from "pixi-spine";
import { resetSpine } from "utils/Misc";
import { Emitter} from "pixi-particles";
import { Graphics, SCALE_MODES, Sprite } from "pixi.js";

export class Precog extends View {

    private background: Sprite;
    private flow: Flow;
    private spec: any;
    private settings: any;
    private interruptible = false;
    private precogSpine: Spine;
    private coinShowerEmitter: Emitter;

    constructor(protected override readonly services: GameServices) {
        super(services);
        const {layout, resources, factory} = services;

        this.spec = resources.getSpec(`precog`);
        this.settings = resources.getSetting(`precog`);

        this.background = this.addChild(this.createBackground());
        this.background.alpha = 0;

        const particleSpec = resources.getSpec(`particles`).precog;
        this.coinShowerEmitter = factory.createParticleEmitter(particleSpec, this);

        this.precogSpine = factory.createSpine(this.spec.precog, this);

        this.flow = new Flow(this);

        this.visible = false;

        layout.onResize.add(this.resize, this);
        this.resize();
    }

    createBackground() {
        const bg = new Graphics();
        bg.beginFill(0x000000, 0.45);
        bg.drawRect(0, 0, 1, 1);
        bg.endFill();
        const background = new Sprite(this.services.application.renderer.generateTexture(bg, {scaleMode: SCALE_MODES.NEAREST, resolution: 1}));
        bg.destroy();
        return background;
    }

    show(anticipationTime: number, shouldCoinShower: boolean) {
        this.flow
            .reset()
            .addCallback(() => this.intro(shouldCoinShower))
            .addDelay(anticipationTime)
            .addCallback(() => this.outro())
            .addDelay(this.settings.fadeOut.duration)
            .addCallback(() => {
                this.visible = false;
                resetSpine(this.precogSpine);
            })
            .run();
    }

    intro(shouldCoinShower: boolean) {
        if (shouldCoinShower) {
            this.coinShowerEmitter.emit = true;
            this.coinShowerEmitter.parent.alpha = 1;
        }
        Tween.get(this.background, {override: true}).to({alpha: 1}, this.settings.fadeIn.duration);
        this.precogSpine.state.setAnimation(0, `0-loop`, true);
        this.precogSpine.state.setAnimation(1, `1-in`, false);
        this.precogSpine.state.tracks[1].timeScale = 1;
        this.interruptible = true;
        this.visible = true;
    }

    outro(timeScale=1) {
        this.coinShowerEmitter.emit = false;
        this.interruptible = false;
        const duration = this.settings.fadeOut.duration / timeScale;
        Tween.get(this.background, {override: true}).to({alpha: 0}, duration);
        Tween.get(this.coinShowerEmitter.parent, {override: true}).to({alpha: 0}, duration);
        this.precogSpine.state.setAnimation(1, `1-out`, false);
        this.precogSpine.state.tracks[1].timeScale = timeScale;
    }

    interrupt() {
        if (this.interruptible) {
            const interruptOutroTimescale = 2;
            this.flow
                .reset()
                .addCallback(() => {
                    this.outro(interruptOutroTimescale);
                })
                .addDelay(this.settings.fadeOut.duration / interruptOutroTimescale)
                .addCallback(() => {
                    this.visible = false;
                    resetSpine(this.precogSpine);
                })
                .run();
        }
    }

    resize() {
        const {layout, resources} = this.services;
        const bounds = layout.getScreenBoundaryInBucketSpace();
        this.background.x = bounds.left;
        this.background.y = bounds.top;
        this.background.width = bounds.width;
        this.background.height = bounds.height;

        const halfWidth = bounds.width / 2;

        this.coinShowerEmitter.spawnRect.width = bounds.width;
        this.coinShowerEmitter.spawnRect.x = -halfWidth;
        this.coinShowerEmitter.spawnPos.x = bounds.left + halfWidth;
        this.coinShowerEmitter.spawnPos.y = bounds.top;

        this.coinShowerEmitter.frequency = 320 / (resources.getSpec(`particles`).precog.particleDensity * bounds.width);
    }

    override reset() {
        this.flow.reset();
        this.interruptible = false;
        this.visible = false;
        resetSpine(this.precogSpine);
    }
}